import {
  BROWSERS,
  getBrowserHtmlContent,
  openDefaultBrowserIntent,
  shouldOpenDefaultBrowser
} from '../browser-utils';
import { AdContainerWebViewProperties } from '../../domain/ad-container.domain';
import {
  IBrowserContainer,
  OpenBrowserOptions
} from '../../domain/browser-container.domain';
import {
  BROWSER_WEBVIEW_CREATED_EVENT,
  disableRagequitTrack,
  sendCustomTrack
} from '../TrackAnalyticsHelper';
import { isFeatureEnable } from '../features-enabling';

export class BrowserStore {
  containerStore: IBrowserContainer;

  constructor(containerStore: IBrowserContainer) {
    this.containerStore = containerStore;
  }

  open(url: string, options: OpenBrowserOptions = {}): void {
    const adOptions = this.containerStore.msAdsOptions;
    const shouldOpenDefault = shouldOpenDefaultBrowser(adOptions, options);
    const useAndroidIntents = isFeatureEnable('androidIntents');
    const useIosDefaultBrowser = isFeatureEnable('iosDefaultBrowser');
    if (shouldOpenDefault && useAndroidIntents) {
      sendCustomTrack(BROWSER_WEBVIEW_CREATED_EVENT);
      const useChromeTab =
        adOptions &&
        typeof adOptions.getParameter === 'function' &&
        adOptions.getParameter('browser') === BROWSERS.CHROME_TAB;
      openDefaultBrowserIntent(url, { useChromeTab });
      this.containerStore.adContainerPort.close &&
        this.containerStore.adContainerPort.close();
      return;
    }
    if (shouldOpenDefault && useIosDefaultBrowser) {
      sendCustomTrack(BROWSER_WEBVIEW_CREATED_EVENT);
      this.containerStore.adContainerPort.openAd(url);
      this.containerStore.adContainerPort.close &&
        this.containerStore.adContainerPort.close();
      return;
    }

    const browserOptions = this.generateBrowserOptions(url, options);

    this.containerStore.adContainerPort.ogyCreateWebView(
      browserOptions,
      this.onWebViewCreated.bind(this)
    );
  }

  onWebViewCreated(err?: Error, res = { webviewId: '' }): void {
    sendCustomTrack('browser_webview_created');

    // Ragequit track is now handled by Ogury browser
    disableRagequitTrack();

    let {
      width: previousWidth,
      height: previousHeight
    } = this.containerStore.adContainerPort.getMaxSize();

    this.initObserver(res.webviewId, previousWidth, previousHeight);
  }

  generateBrowserOptions(
    url: string,
    options: OpenBrowserOptions
  ): AdContainerWebViewProperties {
    let adOptions = this.containerStore.msAdsOptions;
    if (options.forceDefault) {
      adOptions = this.overrideAdOptions(adOptions);
    }

    return {
      content: getBrowserHtmlContent(adOptions, url),
      id: 'browser',
      keepAlive: !!adOptions.getParameter('browser_keep_alive'),
      position: { x: 0, y: 0 },
      size: this.containerStore.adContainerPort.getMaxSize()
    };
  }

  overrideAdOptions(adOptions: FormatOptions): FormatOptions {
    const getParameterRef = adOptions.getParameter;
    adOptions.getParameter = (key: string) => {
      if (key === 'browser') {
        return BROWSERS.DEFAULT_BROWSER;
      }
      return getParameterRef(key);
    };

    return adOptions;
  }

  initObserver(
    webViewId: string,
    previousWidth: number,
    previousHeight: number
  ): void {
    window.addEventListener('resize', () => {
      this.containerStore.adContainerPort.ogyUpdateWebView(webViewId, {
        size: this.containerStore.adContainerPort.getMaxSize()
      });
    });

    this.containerStore.adContainerPort.addEventListener(
      'sizeChange',
      (width: number, height: number) => {
        if (width !== previousWidth || height !== previousHeight) {
          previousHeight = height;
          previousWidth = width;
          this.containerStore.adContainerPort.ogyUpdateWebView(webViewId, {
            size: { width, height }
          });
        }
      }
    );
  }
}
